import { motion } from "motion/react";
import { bool, string } from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

import styled, { css, keyframes } from "styled-components";

import random from "lodash/random";

import { colorIsLight, lightenDarkenColor } from "Utilities/Colors/colorUtils";
import useComponentIsMounted from "Utilities/Hooks/useComponentIsMounted";
import useWidth from "Utilities/Hooks/useWidth";
import dynOpactity from "Utilities/interpolations/dynOpactity";

const createXArray = (width) => {
  const minVal = 5;
  const maxVal = 16;

  // Normalize width into [0,1]
  const t = (width - 90) / (240 - 90);

  // Scale into a max value between 5 and 16
  const allowedMax = Math.round(minVal + t * (maxVal - minVal));

  // Pick the first number between minVal and allowedMax
  let first = random(minVal, allowedMax);

  // Offset 1–4
  let second = first + random(1, 4);

  // Clamp to allowedMax
  if (second > allowedMax) {
    second = allowedMax;
    first = Math.max(minVal, second - random(1, 4));
  }

  return [Math.min(first, second), Math.max(first, second)];
};

const yArray = [10, 30, 20]; // same for all squiggles
const createPath = (width) => {
  const xArray = createXArray(width);
  let pathArray = ["M2,20"];
  let x = Math.ceil(Math.random() * 8) + 10;

  while (x < width - xArray[0] - xArray[1] - xArray[1]) {
    pathArray.push(`C${x},${yArray[0]}`);
    x += xArray[0];
    pathArray.push(`${x},${yArray[1]}`);
    x += xArray[1];
    pathArray.push(`${x},${yArray[2]}`);
    x += xArray[1];
  }

  if (x > width * 0.9 || width - x < 20) return pathArray.join(" ");

  x -= random(xArray[1] - 1, 1);
  pathArray.push(`C${x},${random(yArray[0], yArray[0] + 5)}`);
  x += random(xArray[0], 5);
  x = Math.min(x, width);
  pathArray.push(`${x},${random(yArray[1] - 5, yArray[1])}`);
  x += random(xArray[1], 0);
  x = Math.min(x, width);
  pathArray.push(`${x},${yArray[2]}`);

  return pathArray.join(" ");
};
const height = 40;

const flashStrokeAnimation = (fromColor, toColor) => keyframes`
  from {
    stroke: ${fromColor};
  }
  to {
    stroke: ${toColor};
  }
`;

const Squiggle = ({ color, show, scaleDown, isFlashing }) => {
  const pathRef = useRef();
  const wrapperRef = useRef();
  const width = useWidth(wrapperRef, 0);

  const animationTime = useMemo(() => Math.min(width * 3, 300), [width]);

  const [path, setPath] = useState("");
  const timeoutRef = useRef();

  const isMounted = useComponentIsMounted();

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    if (!show || path.length < 6) {
      timeoutRef.current = setTimeout(
        () => setPath(createPath(width * (scaleDown ? 1.2 : 1))), // we scale it back up so the width stays constant in the SVG.
        animationTime
      );
    }
  }, [animationTime, show, width, scaleDown, isMounted, path.length]);

  const flashToColor = useMemo(
    () => lightenDarkenColor(color, colorIsLight(color) ? -60 : 60),
    [color]
  );

  const flashAnimation = useMemo(
    () =>
      isFlashing
        ? css`
            animation: ${flashStrokeAnimation(color, flashToColor)} 0.5s
              ease-in-out infinite alternate;
          `
        : "",
    [isFlashing, color, flashToColor]
  );

  return (
    <SDiv
      ref={wrapperRef}
      $show={show}
      $opacity={show ? 1 : 0}
      $animationTime={animationTime}
      aria-hidden={!show}
    >
      {Boolean(width) && (
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height={height}
          animate={{
            viewBox: `0 0 ${width * (scaleDown ? 1.3 : 1)} ${height}`,
            transition: { type: "tween", duration: 0.2 },
          }}
          preserveAspectRatio="xMinYMid meet"
        >
          <defs>
            <filter
              id="shadow"
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
              filterUnits="objectBoundingBox"
            >
              <feDropShadow
                dx="1"
                dy="1"
                stdDeviation="1"
                floodColor="black"
                floodOpacity="0.5"
              />
            </filter>
          </defs>
          <StyledPath
            ref={pathRef}
            d={path}
            stroke={color}
            strokeWidth="3"
            fill="none"
            $flashAnimation={flashAnimation}
            style={{
              strokeDashoffset: show ? 0 : width * (scaleDown ? 1.4 : 1.2),
              strokeDasharray: width * (scaleDown ? 1.4 : 1.2),
              transition: `stroke-dashoffset ${animationTime}ms linear`,
              strokeLinecap: "round",
            }}
            filter="url(#shadow)"
          />
        </motion.svg>
      )}
    </SDiv>
  );
};

const SDiv = styled.div`
  position: relative;
  width: max(120%, 70px);
  top: -14px;
  left: -4px;
  height: 0;
  ${dynOpactity}
  ${({ $show, $animationTime }) => {
    return !$show
      ? `transition: opacity 300ms linear ${$animationTime * 2}ms;`
      : "";
  }}
`;

const StyledPath = styled.path`
  ${({ $flashAnimation }) => $flashAnimation}
`;

Squiggle.propTypes = {
  color: string,
  show: bool,
  navSectionKey: string,
  scaleDown: bool,
  isFlashing: bool,
};

export default Squiggle;
